import React from 'react';
import Icon from 'components/AppIcon';
import Input from 'components/ui/Input';

export default function ScenarioBuilder({
  scenarioData,
  onScenarioChange,
  selectedChemical,
  selectedMaterial,
}) {
  const updateScenario = (field, value) => {
    onScenarioChange({ ...scenarioData, [field]: value });
  };

  const scenarios = [
    { id: 'storage', name: 'Storage Tank', icon: 'Database', description: 'Long-term chemical storage' },
    { id: 'process', name: 'Process Line', icon: 'Zap', description: 'Active processing conditions' },
    { id: 'emergency', name: 'Emergency Spill', icon: 'AlertTriangle', description: 'Spill containment scenario' },
    { id: 'transport', name: 'Transport', icon: 'Truck', description: 'Chemical transportation' },
  ];

  return (
    <div className="space-y-6">
      {/* Scenario Templates */}
      <div className="card">
        <h3 className="text-lg font-semibold text-foreground mb-4">Select Scenario Template</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {scenarios?.map((scenario) => (
            <button
              key={scenario?.id}
              className="p-4 border-2 border-border rounded-lg hover:border-primary hover:bg-accent/50 transition-all text-left"
            >
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Icon name={scenario?.icon} size={20} className="text-primary" />
                </div>
                <div>
                  <div className="font-semibold text-foreground mb-1">{scenario?.name}</div>
                  <div className="text-xs text-muted-foreground">{scenario?.description}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
      {/* Environmental Conditions */}
      <div className="card">
        <h3 className="text-lg font-semibold text-foreground mb-4">Environmental Conditions</h3>
        <div className="grid md:grid-cols-2 gap-6">
          {/* Temperature */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Temperature (°C)
            </label>
            <Input
              type="number"
              value={scenarioData?.temperature}
              onChange={(e) => updateScenario('temperature', parseFloat(e?.target?.value))}
              className="mb-2"
            />
            <input
              type="range"
              min="-50"
              max="200"
              value={scenarioData?.temperature}
              onChange={(e) => updateScenario('temperature', parseFloat(e?.target?.value))}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>-50°C</span>
              <span className="font-medium text-foreground">{scenarioData?.temperature}°C</span>
              <span>200°C</span>
            </div>
          </div>

          {/* Pressure */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Pressure (bar)
            </label>
            <Input
              type="number"
              value={scenarioData?.pressure}
              onChange={(e) => updateScenario('pressure', parseFloat(e?.target?.value))}
              className="mb-2"
            />
            <input
              type="range"
              min="0"
              max="100"
              step="0.1"
              value={scenarioData?.pressure}
              onChange={(e) => updateScenario('pressure', parseFloat(e?.target?.value))}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>0 bar</span>
              <span className="font-medium text-foreground">{scenarioData?.pressure} bar</span>
              <span>100 bar</span>
            </div>
          </div>

          {/* Concentration */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Concentration (%)
            </label>
            <Input
              type="number"
              value={scenarioData?.concentration}
              onChange={(e) => updateScenario('concentration', parseFloat(e?.target?.value))}
              className="mb-2"
            />
            <input
              type="range"
              min="0"
              max="100"
              value={scenarioData?.concentration}
              onChange={(e) => updateScenario('concentration', parseFloat(e?.target?.value))}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>0%</span>
              <span className="font-medium text-foreground">{scenarioData?.concentration}%</span>
              <span>100%</span>
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Exposure Duration (hours)
            </label>
            <Input
              type="number"
              value={scenarioData?.duration}
              onChange={(e) => updateScenario('duration', parseFloat(e?.target?.value))}
              className="mb-2"
            />
            <input
              type="range"
              min="1"
              max="8760"
              value={scenarioData?.duration}
              onChange={(e) => updateScenario('duration', parseFloat(e?.target?.value))}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>1h</span>
              <span className="font-medium text-foreground">{scenarioData?.duration}h</span>
              <span>1 year</span>
            </div>
          </div>
        </div>
      </div>
      {/* Additional Parameters */}
      <div className="card">
        <h3 className="text-lg font-semibold text-foreground mb-4">Additional Parameters</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              pH Level
            </label>
            <Input type="number" placeholder="Enter pH (0-14)" step="0.1" />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              UV Exposure
            </label>
            <select className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground">
              <option>None</option>
              <option>Low</option>
              <option>Medium</option>
              <option>High</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Stress Type
            </label>
            <select className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground">
              <option>Static</option>
              <option>Dynamic</option>
              <option>Cyclic</option>
              <option>Impact</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Surface Condition
            </label>
            <select className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground">
              <option>Clean/New</option>
              <option>Used</option>
              <option>Corroded</option>
              <option>Coated</option>
            </select>
          </div>
        </div>
      </div>
      {/* Run Simulation */}
      <div className="card bg-accent/50">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-foreground mb-1">Ready to Run Simulation</h3>
            <p className="text-sm text-muted-foreground">
              {selectedChemical && selectedMaterial
                ? `Testing ${selectedChemical?.name} with ${selectedMaterial?.name}`
                : 'Select chemical and material to begin'}
            </p>
          </div>
          <button
            className="btn-primary flex items-center gap-2"
            disabled={!selectedChemical || !selectedMaterial}
          >
            <Icon name="Play" size={18} />
            Run Simulation
          </button>
        </div>
      </div>
    </div>
  );
}